import { useMemo, useState } from "react";
import { Plus, Loader2, Lock } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  useReceivableTaxes,
  useCreateReceivableTax,
  useUpdateReceivableTax,
  useDeleteReceivableTax,
  useCreateReceivableTaxDocument,
  useDeleteReceivableTaxDocument,
  useReceivableTaxDocuments,
} from "../hooks";
import {
  PendingReceivableTax,
  ReceivableTaxDocument,
  PendingReceivableTaxDocument,
  ReceivableStatus,
} from "../utils/types";
import {
  TaxListItem as TaxListItemComponent,
  TaxCreateDialog,
  TaxEditDialog,
  TaxDeleteDialog,
} from "./taxes";
import {
  TaxListItem,
  TaxFormValues,
  generateTempId,
} from "./taxes/utils";

interface ReceivableTaxesSectionProps {
  receivableId?: string;
  pendingTaxes?: PendingReceivableTax[];
  onPendingTaxCreate?: (tax: PendingReceivableTax) => void;
  onPendingTaxUpdate?: (tax: PendingReceivableTax) => void;
  onPendingTaxDelete?: (id: string) => void;
  isProcessing?: boolean;
  /** Quando o recebível está pendente (ex.: WAITING, RECEIVED), o valor da taxa pode ser editado mesmo para taxas já salvas. */
  receivableStatus?: ReceivableStatus;
}

const PENDING_STATUSES_ALLOW_TAX_VALUE_EDIT: ReceivableStatus[] = [
  ReceivableStatus.WAITING,
  ReceivableStatus.RECEIVED,
];

export function ReceivableTaxesSection({
  receivableId,
  pendingTaxes = [],
  onPendingTaxCreate,
  onPendingTaxUpdate,
  onPendingTaxDelete,
  isProcessing = false,
  receivableStatus,
}: ReceivableTaxesSectionProps) {
  const canEditTaxValue =
    receivableStatus === undefined ||
    PENDING_STATUSES_ALLOW_TAX_VALUE_EDIT.includes(receivableStatus);
  const {
    taxes,
    isLoading: loadingTaxes,
    isFetching: fetchingTaxes,
  } = useReceivableTaxes(receivableId);
  const { createTax, isLoading: creatingTax } = useCreateReceivableTax();
  const { updateTax, isLoading: updatingTax } = useUpdateReceivableTax();
  const { deleteTax, isLoading: deletingTax } = useDeleteReceivableTax();
  const { createTaxDocument, isLoading: creatingTaxDocument } = useCreateReceivableTaxDocument();
  const { deleteTaxDocument, isLoading: deletingTaxDocument } = useDeleteReceivableTaxDocument();
  const isDocumentMutationPending = creatingTaxDocument || deletingTaxDocument;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TaxListItem | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<TaxListItem | null>(null);

  const currentEditingTaxId = useMemo(
    () => (editingItem?.kind === "existing" ? editingItem.tax.id : undefined),
    [editingItem],
  );
  const {
    documents: existingTaxDocuments,
    isLoading: loadingTaxDocuments,
    refetch: refetchTaxDocuments,
  } = useReceivableTaxDocuments(currentEditingTaxId, {
    enabled: Boolean(currentEditingTaxId) && isEditDialogOpen,
  });

  const hasReceivableId = Boolean(receivableId);

  const sortedExistingTaxes = useMemo(() => {
    return [...taxes].sort((a, b) => {
      const aDate = new Date(a.createdAt).getTime();
      const bDate = new Date(b.createdAt).getTime();
      return bDate - aDate;
    });
  }, [taxes]);

  const sortedPendingTaxes = useMemo(() => {
    return [...pendingTaxes].sort((a, b) => {
      const aDate = new Date(a.createdAt).getTime();
      const bDate = new Date(b.createdAt).getTime();
      return bDate - aDate;
    });
  }, [pendingTaxes]);

  const listItems: TaxListItem[] = hasReceivableId
    ? sortedExistingTaxes.map((tax) => ({ kind: "existing", tax }))
    : sortedPendingTaxes.map((tax) => ({ kind: "pending", tax }));

  const isBusy = creatingTax || updatingTax || deletingTax || isProcessing;

  const handleCreateSubmit = async (
    values: TaxFormValues,
    documents: PendingReceivableTax["documents"]
  ) => {
    if (isProcessing) return;

    const payload = {
      currency: values.currency,
      type: values.type,
      description: values.description?.trim() || undefined,
      document: values.document?.trim() || undefined,
      value: values.value,
    };

    if (hasReceivableId && receivableId) {
      try {
        const createdTax = await createTax({
          receivableId,
          ...payload,
        });

        // Upload documentos de forma assíncrona após criar a taxa
        if (documents && documents.length > 0) {
          Promise.all(
            documents.map((document) =>
              createTaxDocument({
                receivableTaxId: createdTax.id,
                documentType: document.documentType,
                description: document.description,
                file: document.file,
              }).catch((error) => {
                console.error("Erro ao anexar documento da taxa:", error);
                return null;
              })
            )
          ).catch(() => {
            // Erros já foram logados individualmente
          });
        }
      } catch (error) {
        console.error("Erro ao criar taxa:", error);
        return;
      }
    } else {
      const pendingTax: PendingReceivableTax = {
        id: generateTempId(),
        currency: payload.currency,
        type: payload.type,
        description: payload.description,
        document: payload.document,
        value: payload.value,
        createdAt: new Date().toISOString(),
        documents: documents?.map((doc) => ({ ...doc })) || [],
      };
      onPendingTaxCreate?.(pendingTax);
    }
    setIsCreateDialogOpen(false);
  };

  const handleEditSubmit = async (
    values: TaxFormValues,
    pendingDocuments: PendingReceivableTax["documents"]
  ) => {
    if (!editingItem || isProcessing) return;

    const payload = {
      currency: values.currency,
      type: values.type,
      description: values.description?.trim() || undefined,
      document: values.document?.trim() || undefined,
      value: values.value,
    };

    if (editingItem.kind === "existing") {
      if (!receivableId) return;
      await updateTax({
        id: editingItem.tax.id,
        receivableId,
        ...payload,
      });
    } else {
      const updated: PendingReceivableTax = {
        ...editingItem.tax,
        ...payload,
        updatedAt: new Date().toISOString(),
        documents: pendingDocuments?.map((doc) => ({ ...doc })) || [],
      };
      onPendingTaxUpdate?.(updated);
    }

    setIsEditDialogOpen(false);
    setEditingItem(null);
  };

  const handleEditDocumentAdd = async (document: {
    documentType: string;
    description?: string;
    file: File;
  }) => {
    if (!editingItem || editingItem.kind !== "existing" || !currentEditingTaxId) return;
    try {
      await createTaxDocument({
        receivableTaxId: currentEditingTaxId,
        documentType: document.documentType as any,
        description: document.description,
        file: document.file,
      });
      refetchTaxDocuments();
    } catch (error) {
      console.error("Erro ao anexar documento da taxa:", error);
    }
  };

  const handleEditDocumentRemove = async (
    document: ReceivableTaxDocument | PendingReceivableTaxDocument
  ) => {
    if (!editingItem) return;

    if (editingItem.kind === "existing" && currentEditingTaxId && "id" in document) {
      try {
        await deleteTaxDocument({ id: document.id, receivableId: currentEditingTaxId });
        refetchTaxDocuments();
      } catch (error) {
        console.error("Erro ao remover documento da taxa:", error);
      }
      return;
    }

    // Para pending, não precisa fazer nada aqui pois será gerenciado pelo hook
  };

  const handleOpenEdit = (item: TaxListItem) => {
    setEditingItem(item);
    setIsEditDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) return;
    if (deleteItem.kind === "existing") {
      if (!receivableId) return;
      await deleteTax({ id: deleteItem.tax.id, receivableId });
    } else {
      onPendingTaxDelete?.(deleteItem.tax.id);
    }
    setDeleteItem(null);
  };

  const canAddTaxes = hasReceivableId || Boolean(onPendingTaxCreate);

  if (!canAddTaxes) {
    return (
      <div className="flex gap-3 rounded-md border border-dashed border-muted-foreground/40 bg-muted/10 p-4 text-sm text-muted-foreground">
        <Lock className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-medium text-foreground">Após salvar o recebível, você poderá adicionar taxas.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Taxas associadas</CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Gerencie impostos, encargos e demais taxas vinculadas ao recebível.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Taxas</h3>
              <p className="text-xs text-muted-foreground">Inclua IOF, DAS, ICMS e outras taxas relacionadas.</p>
            </div>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
              {hasReceivableId && !canEditTaxValue && (
                <span className="text-xs text-muted-foreground">
                  Novas taxas e remoção bloqueadas; você pode editar para ver detalhes e anexar documentos.
                </span>
              )}
              {hasReceivableId && fetchingTaxes && (
                <span className="flex items-center text-xs text-muted-foreground">
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  Atualizando
                </span>
              )}
              <Button
                type="button"
                size="sm"
                onClick={() => setIsCreateDialogOpen(true)}
                disabled={isBusy || (hasReceivableId && !canEditTaxValue)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Nova taxa
              </Button>
            </div>
          </div>

          {hasReceivableId && loadingTaxes && sortedExistingTaxes.length === 0 ? (
            <div className="flex items-center justify-center gap-2 rounded-md border border-dashed border-muted-foreground/40 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando taxas...
            </div>
          ) : listItems.length === 0 ? (
            <div className="flex items-center justify-center rounded-md border border-dashed border-muted-foreground/40 p-6 text-sm text-muted-foreground">
              Nenhuma taxa cadastrada ainda.
            </div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto pr-1">
              <ul className="space-y-2">
                {listItems.map((item) => (
                  <TaxListItemComponent
                    key={`${item.kind}-${item.tax.id}`}
                    item={item}
                    onEdit={handleOpenEdit}
                    onDelete={(item) => setDeleteItem(item)}
                    disabled={isBusy}
                    canDelete={canEditTaxValue}
                  />
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <TaxCreateDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateSubmit}
        disabled={isBusy}
      />

      <TaxEditDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) setEditingItem(null);
        }}
        tax={editingItem}
        existingDocuments={existingTaxDocuments ?? []}
        loadingDocuments={loadingTaxDocuments}
        onSubmit={handleEditSubmit}
        onDocumentAdd={handleEditDocumentAdd}
        onDocumentRemove={handleEditDocumentRemove}
        disabled={isBusy}
        isDocumentMutationPending={isDocumentMutationPending}
        canEditTaxValue={canEditTaxValue}
      />

      <TaxDeleteDialog
        open={Boolean(deleteItem)}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        tax={deleteItem}
        onConfirm={handleConfirmDelete}
        isDeleting={deletingTax || isProcessing}
      />
    </>
  );
}

